/**
 * @description Filterable time entry table for admin/manager dashboard
 *
 * Key features:
 * - Filter by employee, date range, status
 * - Pagination
 * - Edit/delete actions
 * - Create manual entry button
 * - Export CSV button
 * - Daily totals display
 *
 * @dependencies
 * - timeClockService: API calls
 * - PERMISSIONS: Permission constants
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Download, Edit2, Trash2, Clock } from 'lucide-react';
import timeClockService from '../../../services/api/timeClockService';
import PermissionGate from '../../shared/PermissionGate';
import { PERMISSIONS } from '../../../constants/permissionMappings';
import TimeEntryEditModal from './TimeEntryEditModal';
import {
  isPtoEntry,
  formatEntryCalendarDate,
  formatEntryClockTime,
  formatEntryKindLabel,
  entryKindBadgeClass,
} from '../../../utils/timeEntryDisplay';

const TimeEntryList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const hasOpenedFromUrl = useRef(false);
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [editingEntry, setEditingEntry] = useState(null);
  const [isManualModalOpen, setIsManualModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    employeeId: '',
    startDate: '',
    endDate: '',
    status: '',
  });

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 25 };
      if (filters.employeeId) params.employeeId = filters.employeeId;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.status) params.status = filters.status;

      const result = await timeClockService.getTimeEntries(params);
      setEntries(result.entries || []);
      setTotal(result.total || 0);
    } catch (err) {
      console.error('Failed to load time entries', err);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  // Handle openModal=manualTime from URL - only once per navigation
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('openModal') === 'manualTime' && !hasOpenedFromUrl.current && !isManualModalOpen) {
      hasOpenedFromUrl.current = true;
      setIsManualModalOpen(true);
      // Clean up URL but keep the view/tab
      const newUrl = new URL(window.location);
      newUrl.searchParams.delete('openModal');
      navigate(newUrl.pathname + newUrl.search, { replace: true });
    }
  }, [location.search, isManualModalOpen, navigate]);

  const handleExport = async () => {
    try {
      const blob = await timeClockService.exportTimeEntries(filters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `time-entries-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this time entry?')) return;
    try {
      await timeClockService.deleteTimeEntry(id);
      loadEntries();
    } catch (err) {
      console.error('Delete failed', err);
    }
  };

  const formatTime = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const formatDate = (iso) => formatEntryCalendarDate(iso);

  const formatDuration = (minutes) => {
    if (!minutes) return '—';
    const h = Math.floor(minutes / 60);
    const m = Math.round(minutes % 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  const formatClockIn = (entry) => (
    isPtoEntry(entry) ? '—' : formatTime(entry.clockInAt)
  );

  const formatClockOut = (entry) => {
    if (isPtoEntry(entry)) return '—';
    if (!entry.clockOutAt) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-700 uppercase tracking-tight">
          In Progress
        </span>
      );
    }
    return formatTime(entry.clockOutAt);
  };

  const totalPages = Math.ceil(total / 25);

  return (
    <div className="space-y-4">
      {/* Edit Modal */}
      {(editingEntry || isManualModalOpen) && (
        <TimeEntryEditModal
          entry={editingEntry}
          onClose={() => {
            setEditingEntry(null);
            setIsManualModalOpen(false);
            // Reset the URL flag when closing so it can open again if needed
            hasOpenedFromUrl.current = false;
          }}
          onSaved={loadEntries}
        />
      )}
      
      {/* Toolbar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-2 rounded-xl border border-gray-200 shadow-sm">
        <div className="flex flex-wrap items-center gap-2">
          {/* Date Range Filter */}
          <div className="flex items-center bg-gray-50 rounded-lg border border-gray-200 p-1">
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              className="bg-transparent border-none focus:ring-0 text-xs font-bold px-2 py-1 uppercase text-gray-600"
              placeholder="Start date"
            />
            <span className="text-gray-300 text-[10px] font-black px-1">TO</span>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              className="bg-transparent border-none focus:ring-0 text-xs font-bold px-2 py-1 uppercase text-gray-600"
              placeholder="End date"
            />
          </div>

          {/* Status Filter */}
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-xs font-bold text-gray-600 focus:ring-2 focus:ring-brand focus:border-brand"
          >
            <option value="">All Status</option>
            <option value="active">Active (Clocked In)</option>
            <option value="completed">Completed</option>
            <option value="edited">Edited</option>
          </select>
        </div>

        <div className="flex items-center gap-2 px-1">
          <PermissionGate permission={PERMISSIONS.time_clock.export}>
            <button
              onClick={handleExport}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-all text-xs font-bold shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand"
            >
              <Download className="w-3.5 h-3.5 text-gray-400" />
              <span>Export CSV</span>
            </button>
          </PermissionGate>
        </div>
      </div>

      {/* Table - Desktop Only */}
      <div className="hidden xl:block bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-50/50 border-b border-gray-100">
                <th className="text-left px-6 py-4 text-[10px] font-bold text-gray-500 uppercase tracking-widest">Employee</th>
                <th className="text-left px-6 py-4 text-[10px] font-bold text-gray-500 uppercase tracking-widest">Date</th>
                <th className="text-left px-6 py-4 text-[10px] font-bold text-gray-500 uppercase tracking-widest">Clock In</th>
                <th className="text-left px-6 py-4 text-[10px] font-bold text-gray-500 uppercase tracking-widest">Clock Out</th>
                <th className="text-left px-6 py-4 text-[10px] font-bold text-gray-500 uppercase tracking-widest">Worked</th>
                <th className="text-left px-6 py-4 text-[10px] font-bold text-gray-500 uppercase tracking-widest">Type</th>
                <th className="text-left px-6 py-4 text-[10px] font-bold text-gray-500 uppercase tracking-widest">Status</th>
                <th className="text-right px-6 py-4 text-[10px] font-bold text-gray-500 uppercase tracking-widest">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                    <div className="flex flex-col items-center justify-center gap-3">
                      <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full" />
                      <p className="text-sm font-medium">Loading entries...</p>
                    </div>
                  </td>
                </tr>
              ) : entries.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <Clock className="w-8 h-8 text-gray-300" />
                      <p className="font-medium">No time entries found</p>
                    </div>
                  </td>
                </tr>
              ) : (
                entries.map((entry) => (
                  <tr key={entry.id} className="hover:bg-gray-50/50 transition-colors group">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-orange-50 text-orange-700 flex items-center justify-center text-xs font-bold border border-orange-100">
                          {entry.employeeName ? entry.employeeName.split(' ').map(n => n[0]).join('') : '?'}
                        </div>
                        <span className="text-sm font-bold text-gray-900">{entry.employeeName || 'Unknown'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-600">
                      {formatDate(entry.clockInAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatClockIn(entry)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatClockOut(entry)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                      {formatDuration(entry.workedMinutes)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-tight ${entryKindBadgeClass(entry)}`}>
                        {formatEntryKindLabel(entry)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-tight ${
                        entry.status === 'active' ? 'bg-green-50 text-green-700' :
                        entry.status === 'completed' ? 'bg-gray-100 text-gray-700' :
                        entry.status === 'edited' ? 'bg-yellow-50 text-yellow-700' :
                        'bg-red-50 text-red-700'
                      }`}>
                        {entry.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-1 transition-opacity">
                        <PermissionGate permission={PERMISSIONS.time_clock.edit}>
                          <button
                            onClick={() => setEditingEntry(entry)}
                            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                            title="Edit entry"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                        </PermissionGate>
                        <PermissionGate permission={PERMISSIONS.time_clock.delete}>
                          <button
                            onClick={() => handleDelete(entry.id)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                            title="Delete entry"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </PermissionGate>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Card View - Mobile & Tablet */}
      <div className="xl:hidden space-y-3">
        {loading ? (
          <div className="bg-white p-12 rounded-xl border border-gray-200 text-center text-gray-400">
            <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-3" />
            <p className="text-sm font-medium">Loading entries...</p>
          </div>
        ) : entries.length === 0 ? (
          <div className="bg-white p-12 rounded-xl border border-gray-200 text-center text-gray-400">
            <Clock className="w-10 h-10 mx-auto mb-3 opacity-20" />
            <p className="font-medium">No time entries found</p>
          </div>
        ) : (
          entries.map((entry) => (
            <div key={entry.id} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              {/* Card Header */}
              <div className="px-4 py-3 bg-gray-50/50 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-orange-50 text-orange-700 flex items-center justify-center text-xs font-bold border border-orange-100">
                    {entry.employeeName ? entry.employeeName.split(' ').map(n => n[0]).join('') : '?'}
                  </div>
                  <span className="text-sm font-bold text-gray-900">{entry.employeeName || 'Unknown'}</span>
                </div>
                <div className="flex items-center gap-1">
                  <PermissionGate permission={PERMISSIONS.time_clock.edit}>
                    <button
                      onClick={() => setEditingEntry(entry)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  </PermissionGate>
                  <PermissionGate permission={PERMISSIONS.time_clock.delete}>
                    <button
                      onClick={() => handleDelete(entry.id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </PermissionGate>
                </div>
              </div>
              
              {/* Card Body */}
              <div className="p-4 grid grid-cols-2 gap-4">
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Date & Time</p>
                  <p className="text-sm font-bold text-gray-900">{formatDate(entry.clockInAt)}</p>
                  <p className="text-xs text-gray-500">
                    {isPtoEntry(entry)
                      ? 'Approved time off'
                      : `${formatEntryClockTime(entry, 'in')} – ${entry.clockOutAt ? formatEntryClockTime(entry, 'out') : 'In Progress'}`}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Duration</p>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-bold text-gray-900">{formatDuration(entry.workedMinutes)}</p>
                    {!entry.clockOutAt && !isPtoEntry(entry) && (
                      <span className="animate-pulse w-2 h-2 rounded-full bg-green-500" />
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Status</p>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-tight ${
                    entry.status === 'active' ? 'bg-green-50 text-green-700' :
                    entry.status === 'completed' ? 'bg-gray-100 text-gray-700' :
                    entry.status === 'edited' ? 'bg-yellow-50 text-yellow-700' :
                    'bg-red-50 text-red-700'
                  }`}>
                    {entry.status}
                  </span>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Entry Type</p>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-tight ${entryKindBadgeClass(entry)}`}>
                    {formatEntryKindLabel(entry)}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/30 rounded-b-xl">
          <p className="text-sm font-medium text-gray-500">
            Showing <span className="text-gray-900">{((page - 1) * 25) + 1}</span> to <span className="text-gray-900">{Math.min(page * 25, total)}</span> of <span className="text-gray-900">{total}</span> entries
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 text-sm font-semibold border border-gray-300 rounded-lg bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 text-sm font-semibold border border-gray-300 rounded-lg bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimeEntryList;
